import { Injectable } from '@angular/core';
import { DesktopPopupService } from 'common/desktop-popup-service/desktop-popup.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationResult } from 'shared/models/notification/notification-result.model';
import { AudioService } from 'common/audio-service/audio.service';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { CoolLocalStorage } from '@angular-cool/storage';

@Injectable()
export class GenesysCloudWorkItemNotificationsService {
  static newChatInQueueTranslationKey = 'genesysCloudWorkItem.desktopNotifications.newChatInQueue';
  static customerTranslationKey = 'genesysCloudWorkItem.desktopNotifications.customer';
  static incomingChatMessageTranslationKey = 'genesysCloudWorkItem.desktopNotifications.incomingChatMessage';
  static notificationStorageKey = 'genesysNotifications';

  constructor(private _desktopPopupService: DesktopPopupService,
              private _translateService: TranslateService,
              private _audioService: AudioService,
              private _workAreaManager: WorkAreaManager,
              private _localStorageService: CoolLocalStorage) {
  }

  public notifyNewConversation(conversationId: string, conversationStartedTime: string) {
    if (!this._isEligibleForNotification(conversationId, conversationStartedTime)) {
      return;
    }
    this._audioService.playNewChatArrived();
    this._sendTranslatedNotification(GenesysCloudWorkItemNotificationsService.newChatInQueueTranslationKey, {});
    this._updateLastInteractionTimestamp(conversationId, conversationStartedTime);
  }

  public notifyNewEmailConversation(conversationId: string, conversationStartedTime: string): void {
    if (!this._isEligibleForNotification(conversationId, conversationStartedTime)) {
      return;
    }
    this._audioService.playNewEmailArrived();
    this._updateLastInteractionTimestamp(conversationId, conversationStartedTime);
  }

  public notifyNewMessage(visitorName: string, message: string, conversationId: string, messageTimestamp: string): void {
    if (!this._isEligibleForNotification(conversationId, messageTimestamp)) {
      return;
    }
    const selectedWorkItem = this._workAreaManager.getSelectedWorkItem();
    if (selectedWorkItem?.id === conversationId) {
      return;
    }

    const translateParams = {
      senderName: visitorName || this._translateService.instant(GenesysCloudWorkItemNotificationsService.customerTranslationKey),
      sentText: message
    };

    this._audioService.playNewChatMessageArrived();
    this._sendTranslatedNotification(GenesysCloudWorkItemNotificationsService.incomingChatMessageTranslationKey, translateParams);
    this._updateLastInteractionTimestamp(conversationId, messageTimestamp);
  }

  removeNotificationsForWorkitem(conversationId: string) {
    let notifications = this._readNotifications();
    notifications.delete(conversationId);
    this._localStorageService.setItem(GenesysCloudWorkItemNotificationsService.notificationStorageKey, JSON.stringify([...notifications]));
  }

  private _isEligibleForNotification(conversationId: string, lastInteractionTimestamp: string): boolean {
    let notifications = this._readNotifications();
    if (!notifications.has(conversationId)) {
      return true;
    }
    let lastNotificationTimestamp = notifications.get(conversationId);
    return !lastNotificationTimestamp || Date.parse(lastInteractionTimestamp) > Date.parse(lastNotificationTimestamp);
  }

  private _sendTranslatedNotification(translateKey: string, translateParams: object): Promise<NotificationResult> {
    let translatedText = this._translateService.instant(translateKey, translateParams);
    return this._desktopPopupService.sendNotification(translatedText);
  }

  private _updateLastInteractionTimestamp(conversationId: string, lastInteractionTimestamp: string) {
    let notifications = this._readNotifications();
    notifications.set(conversationId, lastInteractionTimestamp);
    this._localStorageService.setItem(GenesysCloudWorkItemNotificationsService.notificationStorageKey, JSON.stringify([...notifications]));
  }

  private _readNotifications(): Map<string, string> {
    const storageData = this._localStorageService.getItem(GenesysCloudWorkItemNotificationsService.notificationStorageKey);
    if (storageData) {
      return new Map(JSON.parse(storageData));
    }

    return new Map<string, string>();
  }
}
